import { useEffect, useState } from "react";
import axios from "axios";
import {
  Activity,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Clock,
  Database,
  Calendar,
  Info,
  ShieldCheck,
} from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Page admin de monitoring — santé des 7 sources de la Veille Proactive.
 *
 * Branchée sur GET /api/admin/veille-proactive/health (étape 6.6).
 * Doctrine "weakest link" : le statut global = pire des 7 statuts.
 *
 * Visuel : palette admin (navy/ivory) cohérente avec les autres pages admin,
 * mais avec une exigence de soin équivalente au reste de la mission Veille.
 */
export default function VeilleProactiveHealthPage() {
  const [loading, setLoading] = useState(true);
  const [report, setReport] = useState(null);
  const [error, setError] = useState(null);

  const fetchHealth = async () => {
    try {
      const token = localStorage.getItem("benchory_token");
      const resp = await axios.get(`${API_URL}/admin/veille-proactive/health`, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 20000,
      });
      setReport(resp.data);
      setError(null);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error("[admin/health] fetch error", e);
      setError(e?.response?.data?.detail || e?.message || "Erreur réseau");
      setReport(null);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchHealth();
  }, []);

  const handleRefresh = () => {
    setLoading(true);
    fetchHealth();
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-8" data-testid="admin-veille-health-page">
      <PageHeader loading={loading} onRefresh={handleRefresh} generatedAt={report?.data?.generated_at} />

      {loading && <HealthSkeleton />}

      {!loading && error && (
        <ErrorBlock message={error} onRetry={handleRefresh} />
      )}

      {!loading && !error && report && (
        <>
          <OverallBlock data={report.data} meta={report.meta} />
          <SourcesTable sources={report.data.sources || []} />
        </>
      )}
    </div>
  );
}


// ── En-tête de page ──────────────────────────────────────────────────────────


function PageHeader({ loading, onRefresh, generatedAt }) {
  return (
    <header className="mb-8 flex items-start justify-between gap-4">
      <div>
        <div className="flex items-center gap-2 mb-2">
          <ShieldCheck size={16} className="text-[#1B2A4A]" strokeWidth={1.7} />
          <span className="text-[11px] uppercase tracking-[0.18em] font-semibold text-[#1B2A4A]/60">
            Monitoring · Veille Proactive
          </span>
        </div>
        <h1 className="font-playfair text-3xl lg:text-4xl text-[#1B2A4A] font-medium tracking-tight">
          Santé des sources publiques
        </h1>
        <p className="mt-2 text-[14px] text-[#1B2A4A]/70 leading-relaxed max-w-2xl">
          Vue consolidée des sept flux externes consommés par la Veille Proactive.
          Le statut global remonte la pire situation détectée (doctrine
          <em> weakest link</em>) pour qu'aucune source en panne ne reste invisible.
        </p>
        {generatedAt && (
          <p className="mt-2 text-[12px] text-[#1B2A4A]/55 inline-flex items-center gap-1.5">
            <Clock size={11} strokeWidth={1.8} />
            Rapport généré le {_formatDateTime(generatedAt)}
          </p>
        )}
      </div>
      <button
        onClick={onRefresh}
        disabled={loading}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-[#1B2A4A]/15 text-[13px] text-[#1B2A4A] hover:bg-[#1B2A4A]/5 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        data-testid="health-refresh-btn"
      >
        <RefreshCw size={13} strokeWidth={1.8} className={loading ? "animate-spin" : ""} />
        Actualiser
      </button>
    </header>
  );
}


// ── Bloc statut global ───────────────────────────────────────────────────────


function OverallBlock({ data, meta }) {
  const status = data?.overall_status || "unknown";
  const message = data?.overall_message;
  const visual = _statusVisual(status);

  return (
    <section
      className="bg-white border rounded-lg p-6 lg:p-8 mb-6"
      style={{ borderColor: visual.border }}
      data-testid="health-overall-block"
    >
      <div className="flex items-start gap-5">
        <div
          className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
          style={{ backgroundColor: visual.bg, color: visual.fg }}
        >
          <visual.Icon size={22} strokeWidth={1.7} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-1">
            <span
              className="text-[10.5px] uppercase tracking-[0.18em] font-semibold"
              style={{ color: visual.fg }}
              data-testid="health-overall-status"
            >
              {_statusLabel(status)}
            </span>
          </div>
          <p className="text-[16px] text-[#1B2A4A] leading-relaxed">
            {message || "État inconnu — vérifiez la connectivité MongoDB."}
          </p>
          <CountsRow meta={meta} />
        </div>
      </div>
    </section>
  );
}


function CountsRow({ meta }) {
  return (
    <div className="mt-4 flex flex-wrap gap-x-6 gap-y-2 text-[12.5px]">
      <span className="inline-flex items-center gap-1.5 text-[#1B2A4A]/75">
        <CheckCircle2 size={12} strokeWidth={1.9} className="text-emerald-600" />
        <strong className="text-[#1B2A4A] tabular-nums">{meta?.healthy_count ?? 0}</strong> à jour
      </span>
      <span className="inline-flex items-center gap-1.5 text-[#1B2A4A]/75">
        <AlertTriangle size={12} strokeWidth={1.9} className="text-amber-600" />
        <strong className="text-[#1B2A4A] tabular-nums">{meta?.stale_count ?? 0}</strong> en léger retard
      </span>
      <span className="inline-flex items-center gap-1.5 text-[#1B2A4A]/75">
        <XCircle size={12} strokeWidth={1.9} className="text-rose-600" />
        <strong className="text-[#1B2A4A] tabular-nums">{meta?.down_count ?? 0}</strong> en panne
      </span>
      <span className="inline-flex items-center gap-1.5 text-[#1B2A4A]/55">
        <Database size={12} strokeWidth={1.9} />
        {meta?.total_sources ?? 0} sources surveillées
      </span>
    </div>
  );
}


// ── Tableau des sources ──────────────────────────────────────────────────────


function SourcesTable({ sources }) {
  if (!sources.length) {
    return (
      <div className="bg-white border border-[#1B2A4A]/10 rounded-lg px-6 py-10 text-center text-[#1B2A4A]/60 text-[13.5px]">
        Aucune source à afficher.
      </div>
    );
  }

  // Tri : par status priority (down > stale > healthy > unknown ailleurs) puis label
  const statusOrder = { down: 0, unknown: 1, stale: 2, healthy: 3 };
  const sorted = [...sources].sort((a, b) => {
    const sa = statusOrder[a.status] ?? 99;
    const sb = statusOrder[b.status] ?? 99;
    if (sa !== sb) return sa - sb;
    return (a.label || "").localeCompare(b.label || "");
  });

  return (
    <section
      className="bg-white border border-[#1B2A4A]/10 rounded-lg overflow-hidden"
      data-testid="health-sources-table"
    >
      <header className="px-6 py-4 border-b border-[#1B2A4A]/8 bg-[#FBF8F3]/50">
        <h2 className="font-playfair text-[18px] text-[#1B2A4A] font-medium tracking-tight">
          Détail par source
        </h2>
      </header>
      <ul className="divide-y divide-[#1B2A4A]/8">
        {sorted.map((s) => (
          <SourceRow key={s.key} source={s} />
        ))}
      </ul>
    </section>
  );
}


function SourceRow({ source }) {
  const visual = _statusVisual(source.status);
  return (
    <li
      className="px-6 py-5"
      data-testid={`health-source-row-${source.key}`}
    >
      <div className="flex items-start gap-4">
        <div
          className="flex-shrink-0 w-9 h-9 rounded-md flex items-center justify-center mt-0.5"
          style={{ backgroundColor: visual.bg, color: visual.fg }}
        >
          <visual.Icon size={16} strokeWidth={1.8} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
            <h3 className="text-[14.5px] font-medium text-[#1B2A4A] tracking-tight">
              {source.label}
            </h3>
            <span
              className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] uppercase tracking-[0.16em] font-semibold"
              style={{ color: visual.fg, backgroundColor: visual.bg, border: `1px solid ${visual.border}` }}
              data-testid={`health-status-badge-${source.key}`}
            >
              {_statusLabel(source.status)}
            </span>
          </div>
          <div className="mt-2 flex flex-wrap gap-x-5 gap-y-1 text-[12px] text-[#1B2A4A]/75">
            <span className="inline-flex items-center gap-1.5">
              <Calendar size={11} strokeWidth={1.9} className="text-[#1B2A4A]/45" />
              {source.schedule}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock size={11} strokeWidth={1.9} className="text-[#1B2A4A]/45" />
              {source.last_refresh ? (
                <>
                  Dernier refresh il y a{" "}
                  <strong className="text-[#1B2A4A] tabular-nums">
                    {_formatAge(source.age_hours)}
                  </strong>{" "}
                  <span className="text-[#1B2A4A]/45">
                    ({_formatDateTime(source.last_refresh)})
                  </span>
                </>
              ) : (
                <span className="italic text-[#1B2A4A]/55">jamais rafraîchie</span>
              )}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Database size={11} strokeWidth={1.9} className="text-[#1B2A4A]/45" />
              <strong className="text-[#1B2A4A] tabular-nums">{(source.count ?? 0).toLocaleString("fr-FR")}</strong> docs · collection
              <code className="text-[11px] text-[#1B2A4A]/70 font-mono">{source.collection}</code>
            </span>
          </div>
          {source.notes && (
            <p
              className="mt-3 inline-flex items-start gap-2 text-[12.5px] text-[#1B2A4A]/75 leading-relaxed bg-[#FBF8F3] border border-[#1B2A4A]/8 rounded-md px-3 py-2"
              data-testid={`health-notes-${source.key}`}
            >
              <Info size={12} className="flex-shrink-0 mt-0.5 text-[#1B2A4A]/50" strokeWidth={1.8} />
              <span>{source.notes}</span>
            </p>
          )}
        </div>
      </div>
    </li>
  );
}


// ── États ────────────────────────────────────────────────────────────────────


function HealthSkeleton() {
  return (
    <div data-testid="health-skeleton">
      <div className="bg-white border border-[#1B2A4A]/10 rounded-lg p-7 mb-6 animate-pulse">
        <div className="flex gap-5">
          <div className="w-12 h-12 rounded-full bg-[#1B2A4A]/8" />
          <div className="flex-1 space-y-3">
            <div className="h-3 w-1/4 rounded bg-[#1B2A4A]/10" />
            <div className="h-5 w-3/4 rounded bg-[#1B2A4A]/12" />
            <div className="h-3 w-1/2 rounded bg-[#1B2A4A]/8" />
          </div>
        </div>
      </div>
      <div className="bg-white border border-[#1B2A4A]/10 rounded-lg p-2 animate-pulse">
        {[0, 1, 2, 3, 4].map((i) => (
          <div key={i} className="px-4 py-5 border-b border-[#1B2A4A]/5 last:border-b-0">
            <div className="flex gap-4">
              <div className="w-9 h-9 rounded-md bg-[#1B2A4A]/8" />
              <div className="flex-1 space-y-2">
                <div className="h-4 w-1/2 rounded bg-[#1B2A4A]/10" />
                <div className="h-3 w-3/4 rounded bg-[#1B2A4A]/6" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function ErrorBlock({ message, onRetry }) {
  return (
    <div
      className="bg-white border border-rose-200 rounded-lg p-8 text-center"
      data-testid="health-error-block"
    >
      <div className="inline-flex w-12 h-12 rounded-full bg-rose-50 items-center justify-center mb-4">
        <XCircle size={20} className="text-rose-600" strokeWidth={1.7} />
      </div>
      <h3 className="font-playfair text-xl text-[#1B2A4A] font-medium tracking-tight">
        Rapport indisponible
      </h3>
      <p className="mt-2 text-[13.5px] text-[#1B2A4A]/75 max-w-md mx-auto leading-relaxed">
        {message}
      </p>
      <button
        onClick={onRetry}
        className="mt-5 inline-flex items-center gap-2 px-4 py-2 rounded-md border border-[#1B2A4A]/20 text-[13px] text-[#1B2A4A] hover:bg-[#1B2A4A]/5 transition-colors"
      >
        <RefreshCw size={13} strokeWidth={1.8} />
        Réessayer
      </button>
    </div>
  );
}


// ── Helpers visuels ──────────────────────────────────────────────────────────


function _statusVisual(status) {
  if (status === "healthy") {
    return { Icon: CheckCircle2, bg: "#ECFDF5", fg: "#047857", border: "#A7F3D0" };
  }
  if (status === "stale") {
    return { Icon: AlertTriangle, bg: "#FEF3C7", fg: "#B45309", border: "#FCD34D" };
  }
  if (status === "down") {
    return { Icon: XCircle, bg: "#FEF2F2", fg: "#B91C1C", border: "#FECACA" };
  }
  return { Icon: Activity, bg: "#F1F5F9", fg: "#475569", border: "#CBD5E1" };
}


function _statusLabel(status) {
  return {
    healthy: "À jour",
    stale: "Léger retard",
    down: "En panne",
    unknown: "État inconnu",
  }[status] || "État inconnu";
}


function _formatAge(hours) {
  if (typeof hours !== "number" || Number.isNaN(hours)) return "—";
  if (hours < 1) return `${Math.round(hours * 60)} min`;
  if (hours < 48) return `${hours.toFixed(1)} h`;
  return `${Math.round(hours / 24)} j`;
}


function _formatDateTime(iso) {
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    const datePart = new Intl.DateTimeFormat("fr-FR", {
      day: "numeric",
      month: "long",
    }).format(d);
    const hh = String(d.getHours()).padStart(2, "0");
    const mm = String(d.getMinutes()).padStart(2, "0");
    return `${datePart} à ${hh}h${mm}`;
  } catch {
    return iso;
  }
}
